<template>
  <v-container fluid class="pa-5">
    <v-overlay :value="isLoading" absolute>
      <div class="d-flex flex-column align-center justify-center">
        <v-progress-circular indeterminate size="64" :width="5" />
        <span class="my-4">กำลังโหลดข้อมูล</span>
      </div>
    </v-overlay>

    <template v-if="!isLoading">
      <v-card class="card-container pa-2" elevation="0">
        <v-card-title>
          <v-row>
            <v-col
              cols="12"
              md="6"
              class="d-flex align-center justify-center justify-md-start"
            >
              <span class="title-text">ข้อความติดต่อเรา</span>
            </v-col>
          </v-row>
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col cols="12" md="4" class="d-flex align-end px-2">
              <v-text-field
                v-model="search"
                placeholder="ค้นหาจากชื่อ / อีเมล / เบอร์โทร / ข้อความ"
                hide-details
                outlined
                dense
                class="rounded-lg"
                clearable
              >
                <template v-slot:append>
                  <v-img
                    src="@/assets/select/search_icon.png"
                    width="16"
                    height="16"
                    class="mt-1"
                    contain
                  />
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12" md="3" class="px-2">
              <span class="title-select-text">สถานะ</span>
              <v-select
                v-model="selectedStatus"
                :items="statusItems"
                :menu-props="{ offsetY: true }"
                placeholder="ทั้งหมด"
                hide-details
                outlined
                dense
                class="rounded-lg"
                item-text="text"
                item-value="value"
              >
                <template v-slot:append>
                  <v-img
                    src="@/assets/select/expand_icon.png"
                    width="16"
                    height="16"
                    contain
                    class="icon-rotate mt-1"
                  />
                </template>
              </v-select>
            </v-col>

            <v-col cols="12" md="2" class="d-flex justify-start align-end px-2">
              <span
                class="clear-btn-text mb-2"
                style="color: #f4811f; cursor: pointer"
                @click="clearFilters"
              >
                ล้างค่า
              </span>
            </v-col>

            <v-col cols="12">
              <span class="count-data-text">
                ข้อความติดต่อ ทั้งหมด {{ filteredContacts.length }} รายการ
              </span>
            </v-col>

            <v-col cols="12">
              <template v-if="filteredContacts.length > 0">
                <AdminContactManagementTable
                  :items="filteredContacts"
                  @view="openView"
                />
              </template>

              <template v-else>
                <v-row>
                  <v-col
                    cols="12"
                    class="d-flex flex-column align-center justify-center"
                    style="min-height: 45vh"
                  >
                    <div class="d-flex flex-column align-center">
                      <v-img>
                        <img
                          src="@/assets/not_exist_search.svg"
                          alt="no-data"
                          width="103.8"
                          height="103.8"
                        />
                      </v-img>
                      <span class="no-data-text mb-4">
                        ไม่พบข้อมูลที่ค้นหา กรุณาลองใหม่อีกครั้ง
                      </span>
                    </div>
                  </v-col>
                </v-row>
              </template>
            </v-col>
          </v-row>

          <AdminContactManagementDialog
            v-model="dialogVisible"
            :item="selectedItem"
            :loading="dialogLoading"
            @save-status="handleSaveStatus"
          />
        </v-card-text>
      </v-card>
    </template>
  </v-container>
</template>

<script>
import AdminContactManagementTable from "@/components/AdminContactManagement/Table.vue";
import AdminContactManagementDialog from "@/components/AdminContactManagement/Dialog.vue";
import { getContactsApi, updateContactApi } from "@/services/api/contactServices";
import swalAlert from "@/services/alert/swalServices";

export default {
  name: "AdminContactManagement",

  components: {
    AdminContactManagementTable,
    AdminContactManagementDialog,
  },

  data() {
    return {
      isLoading: false,
      contacts: [],
      search: "",
      selectedStatus: "all",
      dialogVisible: false,
      dialogLoading: false,
      selectedItem: null,
      statusItems: [
        { text: "ทั้งหมด", value: "all" },
        { text: "ใหม่", value: "ใหม่" },
        { text: "กำลังดำเนินการ", value: "กำลังดำเนินการ" },
        { text: "เสร็จสิ้น", value: "เสร็จสิ้น" },
      ],
    };
  },

  computed: {
    filteredContacts() {
      let result = [...this.contacts];

      if (this.selectedStatus && this.selectedStatus !== "all") {
        result = result.filter((c) => c.status === this.selectedStatus);
      }

      if ((this.search || "").trim()) {
        const kw = this.search.toLowerCase().trim();
        result = result.filter((c) => {
          const name = `${c.firstName || ""} ${c.lastName || ""}`.toLowerCase();
          const email = (c.email || "").toLowerCase();
          const phone = (c.phone || "").toLowerCase();
          const message = (c.message || "").toLowerCase();
          return (
            name.includes(kw) ||
            email.includes(kw) ||
            phone.includes(kw) ||
            message.includes(kw)
          );
        });
      }

      return result;
    },
  },

  async created() {
    await this.loadContacts();
  },

  methods: {
    async loadContacts() {
      this.isLoading = true;
      try {
        const res = await getContactsApi({ limit: 100 });
        this.contacts = Array.isArray(res?.data) ? res.data : [];
      } catch (err) {
        console.error("[AdminContactManagement] loadContacts error:", err);
        this.contacts = [];
        swalAlert.Error(
          "โหลดข้อมูลไม่สำเร็จ",
          err?.response?.data?.message || "ไม่สามารถโหลดข้อมูลการติดต่อได้"
        );
      } finally {
        this.isLoading = false;
      }
    },

    clearFilters() {
      this.search = "";
      this.selectedStatus = "all";
    },

    openView(item) {
      this.selectedItem = item;
      this.dialogVisible = true;
    },

    async handleSaveStatus({ id, status }) {
      this.dialogLoading = true;
      try {
        const res = await updateContactApi(id, { status });
        const updated = res.data || { ...this.selectedItem, status };
        this.contacts = this.contacts.map((c) =>
          c.id === id ? { ...c, ...updated } : c
        );
        await swalAlert.Success("อัปเดตสถานะสำเร็จ");
        this.dialogVisible = false;
      } catch (err) {
        console.error("[AdminContactManagement] save status error:", err);
        swalAlert.Error(
          "บันทึกสถานะไม่สำเร็จ",
          err?.response?.data?.message || "ไม่สามารถอัปเดตสถานะได้"
        );
      } finally {
        this.dialogLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.title-text {
  font-size: 24px;
  font-weight: 700;
  color: #1f2a39;
}

.no-data-text {
  font-size: 14px;
  font-weight: 400;
  color: #6b717f;
}

.card-container {
  border-radius: 16px !important;
  box-shadow: 0 0 2px 0 #1018280d !important;
}

.count-data-text {
  font-size: 16px;
  font-weight: 600;
  color: #394253;
}

.title-select-text {
  font-size: 16px;
  font-weight: 400;
  color: #1f2a39;
}

.clear-btn-text {
  font-size: 14px;
  font-weight: 400;
}

.v-text-field--outlined >>> fieldset,
.v-select--outlined >>> fieldset {
  border-color: #e5e7eb !important;
  border-radius: 8px !important;
}

.v-select .icon-rotate {
  transition: transform 0.3s ease;
  transform: rotate(0deg);
}

.v-select.v-select--is-menu-active .icon-rotate {
  transform: rotate(180deg);
}

::v-deep(.v-select__selections),
::v-deep(.v-text-field__slot),
::v-deep(.v-label) {
  font-size: 14px !important;
  font-weight: 400 !important;
  color: #1f2a39 !important;
}
</style>
